// Модель фигур разметки: хранение, попадание, правка и отрисовка.
// Тот же код живёт в QA-плагине виджета (__shared/plugins/QAReviewPlugin/shapes.js),
// чтобы разметка 2D-сравнения в CRM и разметка в виджете вели себя одинаково.
export const COLOR_PRESETS = ['#ff4d4f', '#fa8c16', '#52c41a', '#1677ff', '#722ed1', '#000000'];
export const LINE_WIDTHS = [2, 4, 6, 10];

/** Радиус попадания по маркеру выделения и отступ ручки поворота от фигуры. */
export const HANDLE_RADIUS = 5;
export const HANDLE_HIT_RADIUS = 9;
export const ROTATE_HANDLE_OFFSET = 26;

const TAU = Math.PI * 2;

const withAlpha = (hex, alpha) => {
  const value = String(hex).replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Отрезок задаётся своими концами, остальные фигуры — рамкой, отсюда разная правка. */
export const isSegmentShape = (shape) => shape?.type === 'line' || shape?.type === 'arrow';

export const shapePoints = (shape) => (shape?.type === 'pen'
  ? (shape.points ?? [])
  : [shape?.start, shape?.end].filter(Boolean));

/** Габариты фигуры без учёта поворота — фигуры так и хранятся, углом их крутит отрисовка. */
export const shapeBounds = (shape) => {
  const points = shapePoints(shape);
  if (!points.length) return null;

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);

  return {
    x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y,
  };
};

export const shapeCenter = (shape) => {
  const bounds = shapeBounds(shape);
  if (!bounds) return null;

  return { x: bounds.x + bounds.w / 2, y: bounds.y + bounds.h / 2 };
};

export const rotatePoint = (point, center, angle) => {
  if (!angle || !center) return { x: point.x, y: point.y };

  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = point.x - center.x;
  const dy = point.y - center.y;

  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos };
};

const boxCorners = (bounds) => [
  { x: bounds.x, y: bounds.y },
  { x: bounds.x + bounds.w, y: bounds.y },
  { x: bounds.x + bounds.w, y: bounds.y + bounds.h },
  { x: bounds.x, y: bounds.y + bounds.h },
];

/** Точки, по которым считается габарит фигуры на экране: у рамки это все четыре угла. */
const outlinePoints = (shape) => {
  if (shape.type === 'pen' || isSegmentShape(shape)) return shapePoints(shape);

  const bounds = shapeBounds(shape);
  return bounds ? boxCorners(bounds) : [];
};

const worldPoints = (shape) => {
  const center = shapeCenter(shape);
  return outlinePoints(shape).map((point) => rotatePoint(point, center, shape.angle ?? 0));
};

/**
 * Маркеры выделения: у рамки — четыре угла, у отрезка — его концы,
 * плюс общая для всех ручка поворота над фигурой.
 */
export const shapeHandles = (shape) => {
  const center = shapeCenter(shape);
  const bounds = shapeBounds(shape);
  if (!center || !bounds) return { points: [], rotate: null };

  const angle = shape.angle ?? 0;
  const rotate = rotatePoint({ x: center.x, y: bounds.y - ROTATE_HANDLE_OFFSET }, center, angle);
  const points = isSegmentShape(shape)
    ? worldPoints(shape)
    : boxCorners(bounds).map((point) => rotatePoint(point, center, angle));

  return { points, rotate };
};

const distanceToSegment = (point, from, to) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSq = dx * dx + dy * dy;

  if (!lengthSq) return Math.hypot(point.x - from.x, point.y - from.y);

  const t = Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSq));

  return Math.hypot(point.x - (from.x + t * dx), point.y - (from.y + t * dy));
};

/** Попадание считаем в собственной системе фигуры: точку «откручиваем» на её угол. */
export const hitTestShape = (shape, point, tolerance = 6) => {
  const center = shapeCenter(shape);
  const bounds = shapeBounds(shape);
  if (!center || !bounds) return false;

  const local = rotatePoint(point, center, -(shape.angle ?? 0));
  const slack = (shape.width ?? 4) / 2 + tolerance;

  if (shape.type === 'rect') {
    return local.x >= bounds.x - slack && local.x <= bounds.x + bounds.w + slack
      && local.y >= bounds.y - slack && local.y <= bounds.y + bounds.h + slack;
  }

  if (shape.type === 'ellipse') {
    const rx = bounds.w / 2 + slack;
    const ry = bounds.h / 2 + slack;
    if (rx <= 0 || ry <= 0) return false;

    const dx = (local.x - center.x) / rx;
    const dy = (local.y - center.y) / ry;
    return dx * dx + dy * dy <= 1;
  }

  if (shape.type === 'pen') {
    const points = shape.points ?? [];
    return points.some((current, index) => (
      index > 0 && distanceToSegment(local, points[index - 1], current) <= slack
    ));
  }

  return distanceToSegment(local, shape.start, shape.end) <= slack;
};

export const moveShape = (shape, dx, dy) => (shape.type === 'pen'
  ? { ...shape, points: (shape.points ?? []).map((point) => ({ x: point.x + dx, y: point.y + dy })) }
  : {
    ...shape,
    start: { x: shape.start.x + dx, y: shape.start.y + dy },
    end: { x: shape.end.x + dx, y: shape.end.y + dy },
  });

/**
 * Правка меняет габариты, а вместе с ними и центр — то есть точку, вокруг которой фигура
 * крутится. Чтобы повёрнутая фигура не уезжала под курсором, сдвигаем её так, чтобы новый
 * центр остался там же, где он был при старом преобразовании.
 */
const reanchor = (next, previousCenter, angle) => {
  const center = shapeCenter(next);
  if (!center || !angle) return next;

  const world = rotatePoint(center, previousCenter, angle);
  return moveShape(next, world.x - center.x, world.y - center.y);
};

export const rotateShapeTo = (shape, point) => {
  const center = shapeCenter(shape);
  if (!center) return shape;

  // Ручка стоит над фигурой, поэтому к углу «центр → курсор» добавляется четверть оборота.
  const angle = Math.atan2(point.y - center.y, point.x - center.x) + Math.PI / 2;

  return { ...shape, angle: ((angle % TAU) + TAU) % TAU };
};

export const resizeShape = (shape, handleIndex, point) => {
  const center = shapeCenter(shape);
  const bounds = shapeBounds(shape);
  if (!center || !bounds) return shape;

  const angle = shape.angle ?? 0;
  const local = rotatePoint(point, center, -angle);

  if (isSegmentShape(shape)) {
    const next = handleIndex === 0
      ? { ...shape, start: local }
      : { ...shape, end: local };

    return reanchor(next, center, angle);
  }

  const fixed = boxCorners(bounds)[(handleIndex + 2) % 4];
  const box = {
    x: Math.min(fixed.x, local.x),
    y: Math.min(fixed.y, local.y),
    w: Math.abs(local.x - fixed.x),
    h: Math.abs(local.y - fixed.y),
  };

  if (shape.type === 'pen') {
    const scaleX = bounds.w ? box.w / bounds.w : 1;
    const scaleY = bounds.h ? box.h / bounds.h : 1;
    const points = (shape.points ?? []).map((current) => ({
      x: box.x + (current.x - bounds.x) * scaleX,
      y: box.y + (current.y - bounds.y) * scaleY,
    }));

    return reanchor({ ...shape, points }, center, angle);
  }

  const next = {
    ...shape,
    start: { x: box.x, y: box.y },
    end: { x: box.x + box.w, y: box.y + box.h },
  };

  return reanchor(next, center, angle);
};

const drawArrowHead = (ctx, from, to, width) => {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const length = Math.max(14, width * 3.5);
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - length * Math.cos(angle - Math.PI / 6), to.y - length * Math.sin(angle - Math.PI / 6));
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - length * Math.cos(angle + Math.PI / 6), to.y - length * Math.sin(angle + Math.PI / 6));
  ctx.stroke();
};

export const drawShape = (ctx, shape) => {
  const color = shape.color ?? COLOR_PRESETS[0];
  const width = shape.width ?? 4;
  const center = shapeCenter(shape);

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = withAlpha(color, 0.08);
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (shape.angle && center) {
    ctx.translate(center.x, center.y);
    ctx.rotate(shape.angle);
    ctx.translate(-center.x, -center.y);
  }

  if (shape.type === 'rect') {
    const bounds = shapeBounds(shape);
    ctx.fillRect(bounds.x, bounds.y, bounds.w, bounds.h);
    ctx.strokeRect(bounds.x, bounds.y, bounds.w, bounds.h);
  }

  if (shape.type === 'ellipse') {
    const bounds = shapeBounds(shape);
    ctx.beginPath();
    ctx.ellipse(center.x, center.y, bounds.w / 2, bounds.h / 2, 0, 0, TAU);
    ctx.fill();
    ctx.stroke();
  }

  if (shape.type === 'line' || shape.type === 'arrow') {
    ctx.beginPath();
    ctx.moveTo(shape.start.x, shape.start.y);
    ctx.lineTo(shape.end.x, shape.end.y);
    ctx.stroke();
    if (shape.type === 'arrow') drawArrowHead(ctx, shape.start, shape.end, width);
  }

  if (shape.type === 'pen' && (shape.points?.length ?? 0) > 1) {
    ctx.beginPath();
    ctx.moveTo(shape.points[0].x, shape.points[0].y);
    shape.points.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
    ctx.stroke();
  }

  ctx.restore();
};

/**
 * Рамка выделения с маркерами. Размеры делятся на масштаб холста, чтобы при зуме
 * маркеры оставались одного размера на экране.
 */
export const drawSelection = (ctx, shape, scale = 1) => {
  const outline = worldPoints(shape);
  const { points, rotate } = shapeHandles(shape);
  if (!outline.length) return;

  const unit = 1 / (scale || 1);

  ctx.save();
  ctx.strokeStyle = '#1677ff';
  ctx.fillStyle = '#ffffff';
  ctx.lineWidth = unit;
  ctx.setLineDash([4 * unit, 3 * unit]);

  ctx.beginPath();
  outline.forEach((point, index) => {
    if (index === 0) ctx.moveTo(point.x, point.y);
    else ctx.lineTo(point.x, point.y);
  });
  if (outline.length > 2) ctx.closePath();
  ctx.stroke();

  ctx.setLineDash([]);

  if (rotate && points.length > 1) {
    // и у рамки, и у отрезка первые два маркера — это верхняя сторона либо сам отрезок
    const anchor = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };

    ctx.beginPath();
    ctx.moveTo(anchor.x, anchor.y);
    ctx.lineTo(rotate.x, rotate.y);
    ctx.stroke();
  }

  [...points, rotate].filter(Boolean).forEach((point) => {
    ctx.beginPath();
    ctx.arc(point.x, point.y, HANDLE_RADIUS * unit, 0, TAU);
    ctx.fill();
    ctx.stroke();
  });

  ctx.restore();
};

/**
 * Габариты набора фигур с запасом на толщину линии — из них выводится bbox замечания
 * там, где привязка считается по нарисованному (разметка поверх панорамы).
 *
 * @param {Array<*>} shapes
 * @param {number} [padding] дополнительный отступ в тех же координатах
 * @returns {?{x: number, y: number, w: number, h: number}}
 */
export const shapesBounds = (shapes, padding = 0) => {
  const points = shapes.filter(Boolean).flatMap((shape) => {
    const pad = (shape.width ?? 4) / 2 + padding;
    return worldPoints(shape).map((point) => ({ ...point, pad }));
  });

  if (!points.length) return null;

  const left = Math.min(...points.map((point) => point.x - point.pad));
  const top = Math.min(...points.map((point) => point.y - point.pad));
  const right = Math.max(...points.map((point) => point.x + point.pad));
  const bottom = Math.max(...points.map((point) => point.y + point.pad));

  return {
    x: left, y: top, w: right - left, h: bottom - top,
  };
};
